import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import type { Customer } from '../lib/types'
import { ChevronIcon } from './Icons'

const PINNED_KEY = 'netzwarte.pinnedCustomers'
const RECENT_KEY = 'netzwarte.recentCustomers'

interface CustomerSwitcherProps {
  customerSlug: string | null
}

/**
 * Globaler Kundenfilter in der Kopfzeile (docs/design.md). Standard ist
 * "Alle Kunden". Die Taste K öffnet die Liste. Angeheftete und zuletzt
 * verwendete Kunden stehen oben; nicht berechtigte Kunden liefert der Server
 * gar nicht erst mit.
 */
export function CustomerSwitcher({ customerSlug }: CustomerSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pinned, setPinned] = useState<string[]>(() => readList(PINNED_KEY))
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })

  // Tastenkürzel K, solange nicht gerade in ein Feld getippt wird.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'k' && e.key !== 'K') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      e.preventDefault()
      setOpen((v) => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    searchRef.current?.focus()
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open])

  const current = customers?.find((c) => c.slug === customerSlug)
  // Solange die Liste lädt – oder wenn die Adresse einen Kunden nennt, den es
  // nicht gibt – bleibt der Kontext trotzdem sichtbar.
  const currentLabel = current?.name ?? customerSlug ?? 'Alle Kunden' 

  const groups = useMemo(() => {
    const all = (customers ?? []).filter((c) =>
      search.trim() === ''
        ? true
        : c.name.toLowerCase().includes(search.trim().toLowerCase()),
    )
    const recent = readList(RECENT_KEY)
    return {
      pinned: all.filter((c) => pinned.includes(c.slug)),
      recent: all.filter((c) => !pinned.includes(c.slug) && recent.includes(c.slug)),
      rest: all.filter((c) => !pinned.includes(c.slug) && !recent.includes(c.slug)),
    }
  }, [customers, search, pinned])

  function choose(slug: string | null) {
    setOpen(false)
    setSearch('')
    if (slug) rememberRecent(slug)
    navigate({ to: pageWithCustomer(pathname, slug) })
  }

  function togglePin(slug: string) {
    setPinned((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
      writeList(PINNED_KEY, next)
      return next
    })
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn"
        data-testid="customer-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Kunde wählen (Taste K)"
      >
        {current ? (
          <span
            className="dot"
            style={{ background: `var(--${current.color || 'cust-1'})`, width: 8, height: 8 }}
          />
        ) : null}
        <span>{currentLabel}</span>
        <ChevronIcon />
      </button>

      {open ? (
        <div
          role="listbox"
          aria-label="Kunde wählen"
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: 380,
            maxHeight: 460,
            overflow: 'auto',
            zIndex: 40,
            boxShadow: '0 12px 32px var(--scrim)',
          }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--line)' }}>
            <label className="sr-only" htmlFor="kundensuche">
              Kunde suchen
            </label>
            <input
              ref={searchRef}
              id="kundensuche"
              className="input"
              type="search"
              placeholder="Kunde suchen"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            type="button"
            role="option"
            aria-selected={!customerSlug}
            className="nav-link"
            style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer' }}
            onClick={() => choose(null)}
          >
            <span style={{ color: 'var(--text)', fontWeight: customerSlug ? 400 : 600 }}>
              Alle Kunden
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              NOC-Ansicht
            </span>
          </button>

          <Group title="Angeheftet" customers={groups.pinned} onChoose={choose} onPin={togglePin} pinned={pinned} current={customerSlug} />
          <Group title="Zuletzt verwendet" customers={groups.recent} onChoose={choose} onPin={togglePin} pinned={pinned} current={customerSlug} />
          <Group title="Alle A–Z" customers={groups.rest} onChoose={choose} onPin={togglePin} pinned={pinned} current={customerSlug} />

          {(customers ?? []).length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <span>Noch keine Kunden angelegt.</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function Group({
  title,
  customers,
  onChoose,
  onPin,
  pinned,
  current,
}: {
  title: string
  customers: Customer[]
  onChoose: (slug: string) => void
  onPin: (slug: string) => void
  pinned: string[]
  current: string | null
}) {
  if (customers.length === 0) return null
  return (
    <>
      <div
        style={{
          padding: '10px 14px 4px',
          fontSize: 12,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title}
      </div>
      {customers.map((c) => (
        <div key={c.id} className="row" style={{ gap: 0 }}>
          <button
            type="button"
            role="option"
            aria-selected={c.slug === current}
            className="nav-link"
            style={{
              flexGrow: 1,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onClick={() => onChoose(c.slug)}
          >
            <span className="row" style={{ gap: 8 }}>
              <span
                className="dot"
                style={{ background: `var(--${c.color || 'cust-1'})`, width: 8, height: 8 }}
              />
              <span style={{ color: 'var(--text)' }}>{c.name}</span>
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              {c.site_count} Standorte · {c.device_count} Geräte
              {c.open_alerts > 0 ? ` · ${c.open_alerts} Alarme` : ''}
            </span>
          </button>
          <button
            type="button"
            className="btn btn-sm"
            style={{ border: 'none', background: 'transparent' }}
            aria-label={pinned.includes(c.slug) ? `${c.name} lösen` : `${c.name} anheften`}
            title={pinned.includes(c.slug) ? 'Lösen' : 'Anheften'}
            onClick={() => onPin(c.slug)}
          >
            {pinned.includes(c.slug) ? '★' : '☆'}
          </button>
        </div>
      ))}
    </>
  )
}

/**
 * Behält die aktuelle Seite bei und tauscht nur den Kundenteil der Adresse.
 */
export function pageWithCustomer(pathname: string, slug: string | null): string {
  const withoutCustomer = pathname.startsWith('/k/')
    ? '/' + pathname.split('/').slice(3).join('/')
    : pathname
  const page = withoutCustomer === '/' ? '' : withoutCustomer.replace(/^\//, '')
  if (!slug) return page ? `/${page}` : '/'
  return page ? `/k/${slug}/${page}` : `/k/${slug}`
}

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function writeList(key: string, value: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value.slice(0, 20)))
  } catch {
    // Ohne lokalen Speicher geht die Merkliste verloren, mehr nicht.
  }
}

function rememberRecent(slug: string) {
  const recent = [slug, ...readList(RECENT_KEY).filter((s) => s !== slug)].slice(0, 6)
  writeList(RECENT_KEY, recent)
}
